import { DatePipe } from '@angular/common';
import { Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { OwnerOptions } from '@core/enums/owner-options.enum';
import { StatusChips } from '@core/models';
import { DocumentCategoryItem } from '@core/models/case-communication/document-category-item.model';
import { DocCatDashboardForm } from '@core/models/case-communication/email-templates-dashboard-form.model';
import { EmailTemplatesDashboard } from '@core/models/case-communication/email-templates.model';
import { appRoutePaths } from '@core/routes/route-paths.constants';
import { ConstantsService, LayoutService } from '@core/services';
import { CaseCommunicationService } from '@core/services/case-communication.service';
import { EmailTemplatesDashboardStoreService } from '@core/services/email-templates-dashboard-store.service';
import { AddEditDocumentCategoryComponent } from '@modules/dialogs/add-edit-document-category/add-edit-document-category.component';
import { indicate, nameof } from '@shared/helpers';
import { searchIncludes } from '@shared/helpers/search.helpers';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-document-categories-tab',
    templateUrl: './document-categories-tab.component.html',
    styleUrls: ['./document-categories-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DocumentCategoriesTabComponent implements OnInit {
  static readonly componentName = 'DocumentCategoriesTabComponent';
  
  @Input() emailTemplatesData: EmailTemplatesDashboard;
  @Input() isMobile: boolean;
  @Input() sysText: any;
  @Input() unsavedChangesSysText: any;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  private destroy$: Subject<void> = new Subject<void>();

  public _dc: MatTableDataSource<DocumentCategoryItem> = new MatTableDataSource<DocumentCategoryItem>();
  public displayedColumns: string[] = ['documentCategoryName','ownerEmployer','allowUse'];
  public pageSizeOptions: number[];
  public pageIndex: number = 0;
  public pageSize: number = 10;
  public isLoadingAddEditDocumentCategoryDialog$: Subject<boolean> = new Subject<boolean>();
  // add and the row edit share the loading flag, so this says which one is running and the template puts the spinner in Add
  // only when Add was pressed
  public loadingAction: 'add' | 'edit';
  public routes: typeof appRoutePaths = appRoutePaths;
  chipStyles: StatusChips;

  // one data source whose data is replaced, plus trackBy, so a refresh keeps the table's rows (and the control that has
  // focus) instead of rebuilding them
  set dataSource(dataSource: DocumentCategoryItem[]) {
    this._dc.data = dataSource;
  }

  public trackByCategoryId(index: number, dc: DocumentCategoryItem): number {
    return dc.documentCategoryId;
  }

  public get form(): FormGroup<DocCatDashboardForm> {
    return this.store.docCatsTabForm;
  }

  public get searchValue(): FormControl<string> {
    return this.form.controls.searchValue as FormControl<string>;
  }

  public get employerControl(): FormControl<number> {
    return this.form.controls.employer as FormControl<number>;
  }

  constructor(
    private store: EmailTemplatesDashboardStoreService,
    private dialog: MatDialog,
    private layoutService: LayoutService,
    private service: CaseCommunicationService,
    private constants: ConstantsService,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.chipStyles = this.constants.STAT_CHIP_STYLES;
    this._dc.sortingDataAccessor = (item: DocumentCategoryItem, property: string) => {
      switch(property) {
        case nameof<DocumentCategoryItem>('documentCategoryName'):
          return item.documentCategoryName.toLowerCase();
        case nameof<DocumentCategoryItem>('ownerEmployer'):
          return item.isSystem
            ? "-- system --"
            : item.isStock
              ? "-- stock --"
              : item.ownerEmployer?.employerName.toLowerCase();
        default:
          return item[property];
      }
    };

    this.store.emailTemplates$
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {  
        if (res != null) {
          this.dataSource = res.documentCategoryItems; 
          this.setPageSizeOptions(res.documentCategoryItems.length);
        }
      });

    if (!this.store.docCatsTabFormNoChanges) {
      this.applyFilters();
    }

    if (this.store.docCatsTabPageData) {
      this.pageIndex = this.store.docCatsTabPageData.pageIndex;
      this.pageSize = this.store.docCatsTabPageData.pageSize;
    }

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=>{
        this.applyFilters();
      })
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.setFilterPredicate();
      this._dc.filter = this.searchValue?.value?.trim().toLowerCase();
      if (this.store.docCatsTabSortData) {
        this.sort.active = this.store.docCatsTabSortData.active;
        this.sort.direction = this.store.docCatsTabSortData.direction;
        this.sort.sortChange.emit();
      }
    }, 0);

    this._dc.sort = this.sort;
    this._dc.paginator = this.paginator;

    this.sort.sortChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((sort: Sort) => {
        if (sort) {
          this.store.docCatsTabSortData = sort;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public applySearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this._dc.filter = filterValue.trim().toLowerCase();
  }

  public onCreateNew(): void {
    this.loadingAction = 'add';
    this.service.getAddEditDocumentCategoryDialog()
      .pipe(indicate(this.isLoadingAddEditDocumentCategoryDialog$))
      .subscribe((res) => {
        const dialogConfig: MatDialogConfig = {
          id: AddEditDocumentCategoryComponent.componentName,
          width: '550px',
          data: { 
            sysText: this.sysText,
            unsavedChangesSysText: this.unsavedChangesSysText,
            dialogData: res
          }
        }

      this.dialog.open(AddEditDocumentCategoryComponent, dialogConfig)
        .afterClosed()
        .pipe(filter((res) => !!res))
        .subscribe((res) => {
          this.store.emailTemplates = res;
      });
    });
  }

  public editCategory(category: DocumentCategoryItem) {
    this.loadingAction = 'edit';
    this.service.getAddEditDocumentCategoryDialog(category.documentCategoryId)
      .pipe(indicate(this.isLoadingAddEditDocumentCategoryDialog$))
      .subscribe((res) => {
        const dialogConfig: MatDialogConfig = {
          id: AddEditDocumentCategoryComponent.componentName,
          width: '550px',
          data: { 
            sysText: this.sysText,
            category: category,
            unsavedChangesSysText: this.unsavedChangesSysText,
            dialogData: res
          }
        }

      this.dialog.open(AddEditDocumentCategoryComponent, dialogConfig)
        .afterClosed()
        .pipe(filter((res) => !!res))
        .subscribe((res) => {
          this.store.emailTemplates = res;
      });
    });
  }

  public changePage(event: PageEvent) {
    this.store.docCatsTabPageData = event;
  }

  private applyFilters() {
    let filteredList = this.store.emailTemplates.documentCategoryItems;

    if (this.employerControl.value !== null) {
      filteredList = filteredList.filter(i => {
        return (i.ownerEmployer?.employerId === this.employerControl.value)
          || (i.isSystem && this.employerControl.value === OwnerOptions.System)
          || (i.isStock && this.employerControl.value === OwnerOptions.Stock)
      });
    }
    
    this.dataSource = filteredList;
    this.setPageSizeOptions(filteredList.length);
    this.setFilterPredicate();
    this._dc.filter = this.searchValue?.value?.trim().toLowerCase();
  }

  private setFilterPredicate() {
    this._dc.filterPredicate = (data, filter) => {
      const filterClean = filter.toLowerCase().trim()
      const dataStr = (`${data.documentCategoryName} ${data.ownerEmployer?.employerName ?? ''} `).toLowerCase();
      return searchIncludes(dataStr, filterClean, this.datePipe); 
    }
  }

  private setPageSizeOptions(length: number) {
    const multipleOf = 10;
    this.pageSizeOptions = [10];
    const multiples = Math.floor(length / multipleOf) + 1;
    for (let i = 2; i <= multiples; i++) {
      this.pageSizeOptions.push(i * multipleOf);
    }
  }
}
